<script setup lang="ts">
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useLocale } from '@/plugins/translation';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export type MenuItemChild = {
  id: number;
  title: string;
  route: string;
};
export type MenuItem = {
  id: number;
  title: string;
  icon: string;
  iconActive: string;
  children?: MenuItemChild[];
  route?: string;
};
type Props = {
  activeSectionId: number;
  hasCarousel: boolean;
  hasTextbooks: boolean;
};

const props = defineProps<Props>();

const { t, l } = useLocale();

const navigationItems = ref<MenuItem[]>([
  {
    id: 1,
    title: l.area_guide.navigation.item_1,
    icon: 'fa-regular fa-thought-bubble',
    iconActive: 'fa-solid fa-thought-bubble',
    children: [
      { id: 1.1, title: l.area_guide.navigation.item_1_1, route: '#section1-1' },
      { id: 1.2, title: l.area_guide.navigation.item_1_2, route: '#section1-2' },
    ],
  },
  {
    id: 2,
    title: l.area_guide.navigation.item_2,
    icon: 'fa-regular fa-head-side-heart',
    iconActive: 'fa-solid fa-head-side-heart',
    children: [
      { id: 2.1, title: l.area_guide.navigation.item_2_1, route: '#section2-1' },
      { id: 2.2, title: l.area_guide.navigation.item_2_2, route: '#section2-2' },
      { id: 2.3, title: l.area_guide.navigation.item_2_3, route: '#section2-3' },
    ],
  },
  {
    id: 4,
    title: l.area_guide.navigation.item_4,
    icon: 'fa-regular fa-square-list',
    iconActive: 'fa-solid fa-square-list',
    route: '#section4',
  },
  {
    id: 3,
    title: l.area_guide.navigation.item_3,
    icon: 'fa-regular fa-video',
    iconActive: 'fa-solid fa-video',
    route: '#section3',
  },
]);

const activeSectionTitle = computed(() => {
  let title = '';

  navigationItems.value.forEach((item) => {
    if (item.id === props.activeSectionId) {
      title = item.title;
    } else if (item.children !== undefined) {
      const activeChild = item.children.find((child) => child.id === props.activeSectionId);

      if (activeChild !== undefined) {
        title = activeChild?.title;
      }
    }
  });

  return title;
});

const isActive = (item: MenuItem | MenuItemChild) => {
  if (props.activeSectionId % 1 === 0) {
    return item.id === props.activeSectionId;
  }

  if (item.id % 1 === 0) {
    return props.activeSectionId - item.id >= 0 && props.activeSectionId - item.id < 1;
  }

  return item.id === props.activeSectionId;
};
const openedListGroup = computed(() => [Math.floor(props.activeSectionId)]);

const emit = defineEmits<{
  scrollToAnchor: [item: MenuItem | MenuItemChild];
}>();
const onMenuItemClick = (item: MenuItem | MenuItemChild) => {
  emit('scrollToAnchor', item); // TODO Vue3
};

onBeforeMount(() => {
  if (props.hasCarousel) {
    navigationItems.value
      .find((item) => item.id === 1)
      ?.children?.splice(-1, 0, { id: 1.3, title: l.area_guide.navigation.item_1_3, route: '#section1-3' });
  }
});

watch(
  () => props.hasTextbooks,
  (hasTextbooks) => {
    if (hasTextbooks) {
      navigationItems.value.push({
        id: 5,
        title: l.film.tabs.immerse,
        icon: 'fa-regular fa-book-open-cover',
        iconActive: 'fa-solid fa-book-open-cover',
        route: '#section5',
      });
    }
  },
  { immediate: true },
);

defineExpose({ activeSectionTitle });
</script>

<template>
  <nav :aria-label="t(l.area_guide.label_side_menu)">
    <v-list :opened="openedListGroup" role="list" :aria-label="t(l.area_guide.label_side_menu)">
      <template v-for="item in navigationItems">
        <li v-if="item.children" :key="item.id">
          <v-list-group :value="item.id">
            <template #activator="{ props, isOpen }">
              <v-list-item
                v-bind="props"
                role="button"
                tabindex="0"
                :aria-expanded="isOpen"
                :aria-selected="undefined"
                :aria-controls="`submenu-${item.id}`"
                :class="{ active: isOpen }"
                @click.stop="onMenuItemClick(item.children[0])"
                @keydown.enter.stop.prevent="onMenuItemClick(item.children[0])"
                @keydown.space.stop.prevent="onMenuItemClick(item.children[0])"
              >
                <template #prepend>
                  <FontAwesomeIcon
                    color="on-surface"
                    v-if="isActive(item)"
                    :icon="item.iconActive"
                    size="lg"
                    class="mr-8"
                  />
                  <FontAwesomeIcon v-else color="on-surface-medium" :icon="item.icon" size="lg" class="mr-8" />
                </template>
                <v-list-item-title class="text-on-surface-medium">{{ t(item.title) }}</v-list-item-title>
                <template #append>
                  <FontAwesomeIcon
                    color="rgb(var(--v-theme-outline))"
                    size="2xs"
                    icon="fa-solid fa-circle"
                  ></FontAwesomeIcon>
                </template>
              </v-list-item>
            </template>
            <ul :id="`submenu-${item.id}`" :aria-label="`${t(item.title)} ${t(l.area_guide.label_side_menu_sub_menu)}`">
              <li v-for="child in item.children" :key="child.id">
                <v-list-item
                  role="button"
                  tabindex="0"
                  :class="[{ active: isActive(child) }, 'submenu-item']"
                  @click.stop="onMenuItemClick(child)"
                  @keydown.enter.stop.prevent="onMenuItemClick(child)"
                  @keydown.space.stop.prevent="onMenuItemClick(child)"
                  :aria-label="t(child.title)"
                >
                  <v-list-item-title>{{ t(child.title) }}</v-list-item-title>
                  <template #append>
                    <FontAwesomeIcon
                      v-if="isActive(child)"
                      color="rgb(var(--v-theme-on-surface))"
                      icon="fa-solid fa-circle"
                      size="lg"
                    />
                    <FontAwesomeIcon size="2xs" v-else color="rgb(var(--v-theme-outline))" icon="fa-solid fa-circle" />
                  </template>
                </v-list-item>
              </li>
            </ul>
          </v-list-group>
        </li>

        <li v-else :key="item.title">
          <v-list-item
            :class="[{ active: isActive(item) }, 'list-item--solo']"
            role="button"
            @click.stop="onMenuItemClick(item)"
            @keydown.enter.stop.prevent="onMenuItemClick(item)"
            @keydown.space.stop.prevent="onMenuItemClick(item)"
          >
            <template #prepend>
              <FontAwesomeIcon
                fixedWidth
                class="mr-8"
                v-if="isActive(item)"
                color="rgb(var(--v-theme-on-surface))"
                :icon="item.iconActive"
              ></FontAwesomeIcon>
              <FontAwesomeIcon
                v-else
                fixedWidth
                class="mr-8"
                color="rgb(var(--v-theme-outline))"
                :icon="item.icon"
              ></FontAwesomeIcon>
            </template>
            <v-list-item-title>{{ t(item.title) }}</v-list-item-title>
            <template #append>
              <FontAwesomeIcon
                size="lg"
                v-if="isActive(item)"
                color="rgb(var(--v-theme-on-surface))"
                icon="fa-solid fa-circle"
              />
              <FontAwesomeIcon size="2xs" v-else color="rgb(var(--v-theme-outline))" icon="fa-solid fa-circle" />
            </template>
          </v-list-item>
        </li>
      </template>
    </v-list>
  </nav>
</template>

<style scoped lang="scss">
@use '@/foundations/theme/settings';

.expandable-menu {
  &.sticky {
    position: sticky;
    z-index: 10;
    top: 154px;
  }
}

:deep(.v-list-item) {
  min-height: 50px;
  padding: 0 8px;
  align-items: center;
  overflow: hidden;

  .v-list-item__icon {
    margin: 0 0 0 14px;
    align-self: center;
    &:first-child {
      margin: 0 16px 0 0;
    }
  }

  &.active.submenu-item {
    color: rgb(var(--v-theme-on-surface));
  }

  .v-list-item__title {
    font-size: 1.125rem;
    line-height: 1.25;
    font-weight: 500;
  }

  .v-list-group__header__append-icon {
    display: none;
  }

  &:before {
    content: '';
    clear: both;
    position: absolute;
    right: 26px;
    top: 0;
    left: unset !important;
    opacity: 1;
    border: 2px solid rgb(var(--v-theme-outline));
    border-radius: 0;
    width: 4px;
    height: 100%;
  }

  .v-list-item {
    padding: 0;

    &__append {
      width: 24px;
      justify-content: center;

      svg {
        position: relative;
      }
    }
  }
}
</style>
